import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { processDynamicField } from 'helpers/field-config/process-dynamic-content';
import { processFields, toQuestion } from 'helpers/field-config/process-static-content';
import { CONFIG_PROCESSORS } from 'helpers/field-config/processors';
import { readValueByName } from 'helpers/field-paths';
import { componentMap, getComponentProps } from 'helpers/question-config';
import { Controller, useWatch } from 'react-hook-form';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';

const withRelaxedRequiredWhenAlternativeFieldPresent = (field, relax) => {
  if (!relax || !field?.required) return field;
  const alt = field.alternativeField ?? field.alternative_field;
  if (typeof alt !== 'string' || !alt.trim()) return field;
  return { ...field, required: false };
};

const FieldConfigRenderer = ({
  // Required props
  fields,
  control,
  register,
  setValue,
  getValues,
  watch,
  formValuesPrefix,

  // Optional props with defaults
  errors = {},
  company = null,
  enabledCompanyChecks = null,
  companyCheckConfigurations = null,
  companyCheckConfig = null,
  candidate = null,
  checkDetails = null,
  subCheckDetails = null,
  candidateValues = null,
  hideOptionalTag = false,
  titleSize = 'small',
  gap = 'small',
  noMargin = true,
  // Callback for hidden fields - components render them separately (outside ToggleSwitchInput to avoid triggering Collapse)
  onHiddenFieldsRender = null,
  allowEmptyRequiredWhenDataSourceAlternative = false,
  onSelectedOptionsContextChange = null,
}) => {
  // processFields camelCases field config keys except `code` (snake_case, matches extra_data) and `options`
  // (resolved option rows keep API/join keys). Visibility uses camelCasedFields.
  const camelCasedFields = useMemo(() => {
    return processFields(fields);
  }, [fields]);

  // Watch the whole prefix so nested updates (e.g. configuration.multiple_office_codes) reliably
  // trigger visibility re-evaluation; useWatch is more dependable than watch(prefix) alone here.
  const watchedPrefixValues = useWatch({
    control,
    name: formValuesPrefix || undefined,
    defaultValue: undefined,
  });

  const formValues =
    (formValuesPrefix != null && formValuesPrefix !== ''
      ? (watchedPrefixValues ?? watch(formValuesPrefix))
      : watch(formValuesPrefix)) || {};

  // Context for evaluating alternative_source conditions (no selectedOptionsContext to avoid cycle)
  const optionsFilterContext = useMemo(
    () => ({
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
    ],
  );

  // When conditions match, use alternative_source (values or options) as the options list (e.g. volunteer → Probity only)
  const effectiveOptionsMap = useMemo(() => {
    const map = {};
    if (!camelCasedFields?.length) return map;

    for (const field of camelCasedFields) {
      const raw = Array.isArray(field.options) ? field.options : (field.options?.options ?? []);
      const altSource = field.alternative_source ?? field.alternativeSource;

      if (
        altSource?.conditions?.length &&
        evaluateFieldConditions(altSource.conditions, formValues, optionsFilterContext)
      ) {
        const values = altSource.values ?? [];
        const optionsList = altSource.options;
        if (Array.isArray(optionsList) && optionsList.length > 0) {
          map[field.code] = optionsList;
        } else if (Array.isArray(values) && values.length > 0) {
          map[field.code] = values.map(v => ({ value: v, name: v }));
        } else {
          map[field.code] = raw;
        }
      } else {
        map[field.code] = raw;
      }
    }
    return map;
  }, [camelCasedFields, formValues, optionsFilterContext]);

  const optionsLookup = useMemo(() => {
    const lookup = {};
    if (!camelCasedFields?.length) return lookup;

    for (const field of camelCasedFields) {
      const raw = Array.isArray(field.options) ? field.options : (field.options?.options ?? []);
      lookup[field.code] = effectiveOptionsMap[field.code] ?? raw;
    }
    return lookup;
  }, [camelCasedFields, effectiveOptionsMap]);

  // Build a context of selected options for template resolution and condition evaluation
  // Maps field code -> selected option object (with all properties like notes, send_destination, etc.)
  const selectedOptionsContext = useMemo(() => {
    const context = {};
    if (!camelCasedFields?.length) return context;

    for (const field of camelCasedFields) {
      const selectedValue = formValues?.[field.code];
      if (!selectedValue) continue;

      // Get options for this field. Fallback: if this field has no options in lookup (e.g. not resolved),
      // use another field's options that contains the selected value (e.g. same option set, different field).
      let options = optionsLookup[field.code] || [];
      if (options.length === 0 && selectedValue != null) {
        const otherWithMatch = Object.entries(optionsLookup).find(
          ([code, opts]) =>
            code !== field.code &&
            Array.isArray(opts) &&
            opts.some(
              opt => opt?.value === selectedValue || String(opt?.value) === String(selectedValue),
            ),
        );
        if (otherWithMatch) options = otherWithMatch[1];
      }
      // Find the selected option object (resilient: strict match, then string match, then trimmed)
      const selectedOption =
        options.find(opt => opt.value === selectedValue) ??
        options.find(opt => String(opt?.value) === String(selectedValue)) ??
        options.find(
          opt =>
            String(opt?.value).trim().toLowerCase() === String(selectedValue).trim().toLowerCase(),
        );

      if (selectedOption) {
        context[field.code] = selectedOption;
      }
    }
    return context;
  }, [camelCasedFields, formValues, optionsLookup, watchedPrefixValues]);

  useEffect(() => {
    onSelectedOptionsContextChange?.(selectedOptionsContext);
  }, [selectedOptionsContext, onSelectedOptionsContextChange]);

  // Build field context for template resolution and condition evaluation
  // Aligns with field_resolver_service data sources: pass `company` through as-is so `company.*`
  // in conditions/templates matches what callers serialize (e.g. admin company payload).
  // Form values are included so {{fieldCode}} in section_help_text (e.g. hidden field codes) resolves in toQuestion.
  // Prefer selectedOptionsContext when a key exists in both, so select/multiple_choice keep option objects and
  // {{fieldCode.name}} etc. still resolve; use formValues only for keys not in selectedOptionsContext (e.g. hidden fields).
  const formAndOptionSources = useMemo(() => {
    const merged = {};
    const form = formValues || {};
    const opts = selectedOptionsContext || {};
    for (const key of new Set([...Object.keys(opts), ...Object.keys(form)])) {
      merged[key] = opts[key] ?? form[key];
    }
    return merged;
  }, [formValues, selectedOptionsContext]);

  const fieldContext = useMemo(
    () => ({
      sources: {
        company,
        enabled_company_checks: enabledCompanyChecks ?? {},
        company_check_configurations: companyCheckConfigurations ?? {},
        configuration: companyCheckConfig,
        candidate,
        candidate_values: candidateValues,
        details: checkDetails,
        subcheck_details: subCheckDetails,
        ...formAndOptionSources,
      },
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
      formAndOptionSources,
    ],
  );

  const conditionContext = useMemo(
    () => ({
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
      selectedOptionsContext,
    }),
    [
      company,
      enabledCompanyChecks,
      companyCheckConfigurations,
      companyCheckConfig,
      candidate,
      candidateValues,
      checkDetails,
      subCheckDetails,
      selectedOptionsContext,
    ],
  );

  const isFieldVisible = useCallback(
    field => {
      if (!field.conditions) return true;
      try {
        return evaluateFieldConditions(field.conditions, formValues, conditionContext);
      } catch (error) {
        console.error('Error evaluating field conditions:', error, field);
        return true;
      }
    },
    [formValues, conditionContext],
  );

  // Check if a field should be treated as hidden due to single option
  // Uses effective options (after alternative_source) so e.g. volunteer -> only Probity -> single option -> hide
  const shouldHideAsSingleOption = useCallback(
    field => {
      const hasOptions = field.options !== undefined && field.options !== null;
      if (!hasOptions) return false;

      const hasHideIfSingleOption = field.hideIfSingleOption;
      if (!hasHideIfSingleOption && !field.required) return false;

      const options = optionsLookup[field.code] ?? [];
      return Array.isArray(options) && options.length <= 1;
    },
    [optionsLookup],
  );

  // Used when splitting visible/hidden and in single-option effect (checks.N.details context)
  const isCheckDetailsPrefix = /^checks\.\d+\.details$/.test(formValuesPrefix || '');

  // Separate hidden fields from visible fields - calculated at render time
  // Fields with hideIfSingleOption and single option are treated as hidden
  // In check details (e.g. ChecksSelector bundle), keep multi_select always visible so we never
  // mount a hidden Controller that could overwrite bundle values with a single-option default
  const visibleFields =
    camelCasedFields?.filter(f => {
      const wouldHide = shouldHideAsSingleOption(f);
      const keepVisibleInCheckDetails = isCheckDetailsPrefix && f.type === 'multi_select';
      return f.type !== 'hidden' && isFieldVisible(f) && (keepVisibleInCheckDetails || !wouldHide);
    }) || [];
  const hiddenFields =
    camelCasedFields?.filter(
      f =>
        (f.type === 'hidden' || shouldHideAsSingleOption(f)) &&
        isFieldVisible(f) &&
        !(isCheckDetailsPrefix && f.type === 'multi_select'),
    ) || [];

  // Identify hidden fields with processors
  const processorFields = useMemo(
    () => hiddenFields.filter(f => f.runtimeConfig?.processor),
    [hiddenFields],
  );

  // Identify hidden fields from hideIfSingleOption OR required fields with single option - need to auto-select value
  const singleOptionFields = useMemo(() => {
    const filtered = hiddenFields.filter(f => {
      if (f.runtimeConfig?.processor) return false;
      // Include fields with hideIfSingleOption OR required fields that end up with single option
      return f.hideIfSingleOption || f.required;
    });
    return filtered;
  }, [hiddenFields]);

  // Track previous processor values to avoid unnecessary setValue calls
  const prevProcessorValuesRef = useRef({});
  const prevSingleOptionValuesRef = useRef({});

  // Run processors when dependencies change (dynamic value computation)
  useEffect(() => {
    if (processorFields.length === 0) return;

    processorFields.forEach(field => {
      const { processor } = field.runtimeConfig;
      const processorFn = CONFIG_PROCESSORS[processor];

      if (!processorFn) return;

      const result = processorFn(formValues, fieldContext.sources);

      // Only update if value has changed
      const fieldPath = `${formValuesPrefix}.${field.code}`;
      const prevValue = prevProcessorValuesRef.current[field.code];

      // Compare using JSON for objects/arrays, direct comparison for primitives
      const hasChanged =
        typeof result === 'object'
          ? JSON.stringify(result) !== JSON.stringify(prevValue)
          : result !== prevValue;

      if (hasChanged) {
        prevProcessorValuesRef.current[field.code] = result;
        setValue(fieldPath, result);
      }
    });
  }, [processorFields, formValues, fieldContext, formValuesPrefix, setValue]);

  // Auto-select value for hideIfSingleOption fields (use effective options so alternative_source single option is set)
  // Skip multi_select when rendering check details (checks.N.details) so we never overwrite bundle/existing values.
  useEffect(() => {
    if (singleOptionFields.length === 0) return;

    singleOptionFields.forEach(field => {
      const options = optionsLookup[field.code] ?? [];
      if (!Array.isArray(options) || options.length !== 1) return;

      const isMultiSelect = field.type === 'multi_select';
      if (isMultiSelect && isCheckDetailsPrefix) return;

      const optionValue = options[0]?.value ?? options[0];
      const singleValue = isMultiSelect ? [optionValue] : optionValue;
      const fieldPath = `${formValuesPrefix}.${field.code}`;
      const prevValue = prevSingleOptionValuesRef.current[field.code];

      const hasChanged =
        typeof singleValue === 'object'
          ? JSON.stringify(singleValue) !== JSON.stringify(prevValue)
          : singleValue !== prevValue;
      if (hasChanged) {
        prevSingleOptionValuesRef.current[field.code] = singleValue;
        setValue(fieldPath, singleValue);
      }
    });
  }, [singleOptionFields, optionsLookup, formValuesPrefix, setValue, isCheckDetailsPrefix]);

  // ============================================================================
  // Rendering
  // ============================================================================

  // Render hidden field with Controller
  const renderHiddenField = useCallback(
    field => {
      const fieldName = `${formValuesPrefix}.${field.code}`;
      const fieldForQuestion = withRelaxedRequiredWhenAlternativeFieldPresent(
        field,
        allowEmptyRequiredWhenDataSourceAlternative,
      );
      const question = toQuestion(fieldForQuestion, true, fieldContext);
      const defaultFromConfig = question.defaultValue ?? field.defaultValue ?? field.default_value;

      // Prefer existing form value (e.g. bundle details) so hidden Controller doesn't overwrite.
      // Use getValues so we read current form state (effect may run with stale formValues closure).
      const currentDetails = formValuesPrefix ? getValues(formValuesPrefix) : undefined;
      const existing = currentDetails?.[field.code] ?? formValues[field.code];
      const hasExistingArray =
        isCheckDetailsPrefix &&
        field.type === 'multi_select' &&
        Array.isArray(existing) &&
        existing.length > 0;

      let defaultValue;
      if (hasExistingArray) {
        defaultValue = existing;
      } else if (field.runtimeConfig?.processor) {
        // Dynamic processor value
        const processorFn = CONFIG_PROCESSORS[field.runtimeConfig.processor];
        if (processorFn) {
          defaultValue = processorFn(formValues, fieldContext.sources);
        }
      } else if (field.hideIfSingleOption) {
        const options = optionsLookup[field.code] ?? [];
        if (Array.isArray(options) && options.length === 1) {
          const optionValue = options[0]?.value ?? options[0];
          defaultValue = field.type === 'multi_select' ? [optionValue] : optionValue;
        } else {
          defaultValue = defaultFromConfig;
        }
      } else {
        defaultValue = defaultFromConfig;
      }

      return (
        <Controller
          key={field.code}
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          render={() => null}
        />
      );
    },
    [
      formValuesPrefix,
      fieldContext,
      formValues,
      control,
      optionsLookup,
      isCheckDetailsPrefix,
      getValues,
      allowEmptyRequiredWhenDataSourceAlternative,
    ],
  );

  // Call onHiddenFieldsRender callback in useEffect to avoid setState during render
  useEffect(() => {
    if (onHiddenFieldsRender && hiddenFields.length > 0) {
      onHiddenFieldsRender(hiddenFields.map(renderHiddenField));
    }
  }, [onHiddenFieldsRender, hiddenFields, renderHiddenField]);

  // ============================================================================
  // Rendering Helpers
  // ============================================================================

  // Render visible field
  const renderVisibleField = (field, index) => {
    const fieldName = `${formValuesPrefix}.${field.code}`;

    // Check if field has dynamicDependencyCodes for dynamic options
    const hasWatchFields =
      field.dynamicDependencyCodes?.length > 0 || field.options?.dynamicDependencyCodes?.length > 0;

    let processedField = field;
    let currentFormValues = formValues;

    // For dynamic_array fields, process field and subFields (dynamic sources only)
    if (field.type === 'dynamic_array' && field.subFields) {
      processedField = processDynamicField(field, formValues, optionsLookup, fieldContext);
      currentFormValues = formValues;
    }
    // For fields with dynamicDependencyCodes, resolve options dynamically based on form values
    else if (hasWatchFields) {
      // Get current values for watched fields
      const watchFields =
        field.dynamicDependencyCodes || field.options?.dynamicDependencyCodes || [];
      currentFormValues = {};
      watchFields.forEach(f => {
        const path = formValuesPrefix ? `${formValuesPrefix}.${f}` : f;
        currentFormValues[f] = getValues(path);
      });

      processedField = processDynamicField(field, currentFormValues, optionsLookup, fieldContext);
    }

    // Use effective options (e.g. after alternative_source) so dropdown shows correct list
    const effectiveOptions = optionsLookup[field.code];
    if (Array.isArray(effectiveOptions) && effectiveOptions.length > 0) {
      processedField = { ...processedField, options: effectiveOptions };
    }

    const fieldForQuestion = withRelaxedRequiredWhenAlternativeFieldPresent(
      processedField,
      allowEmptyRequiredWhenDataSourceAlternative,
    );
    const question = toQuestion(fieldForQuestion, true, fieldContext);

    // Prefer existing form value over config default so pre-filled data (e.g. bundle details in
    // ChecksSelector, or existing values in AdminCompanyCheck/CandidateCompletionModal) is used.
    const existingValue = formValues[field.code];
    const questionWithValue =
      existingValue !== undefined && existingValue !== null
        ? { ...question, defaultValue: existingValue }
        : question;

    // Calculate dynamic props based on field type
    const isDynamicArray = field.type === 'dynamic_array' && field.subFields;
    const isArrayType = ['array', 'dynamic_array'].includes(field.type);
    const isDynamicField = isDynamicArray || hasWatchFields;

    const allValues = isDynamicArray || isArrayType ? { [fieldName]: watch(fieldName) } : undefined;
    const wrapperNoMargin = isDynamicField ? true : noMargin;
    const wrapperGap = isDynamicField ? 'small' : gap;
    const wrapperHideHeader = isDynamicField ? false : field.type === 'hidden';

    // Extract error from errors object using field name (fallback for React Hook Form Controller)
    const fieldError = readValueByName(errors, fieldName);

    // Key that includes dependency values so statement/text that resolves from selected options re-renders when selection changes
    const dependencyKey =
      hasWatchFields &&
      (field.dynamicDependencyCodes || field.options?.dynamicDependencyCodes)?.length
        ? (field.dynamicDependencyCodes || field.options?.dynamicDependencyCodes)
            .map(dep => formValues?.[dep])
            .filter(Boolean)
            .join('-')
        : '';
    const rowKey = dependencyKey ? `${field.code}-${dependencyKey}` : field.code;

    return (
      <div key={rowKey} className="u-margin-bottom">
        <QuestionWrapper
          question={questionWithValue}
          questionIndex={index}
          fieldName={fieldName}
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          componentMap={componentMap}
          errors={errors}
          error={fieldError}
          hideHeader={wrapperHideHeader}
          hideOptionalTag={hideOptionalTag}
          requirementVariantStyle="text"
          titleSize={titleSize}
          isVisible={true}
          allValues={allValues}
          sectionHeader={questionWithValue.sectionHeader}
          textParams={formValues}
          noMargin={wrapperNoMargin}
          gap={wrapperGap}
          getComponentProps={value =>
            getComponentProps(questionWithValue, value, null, {}, null, true, {}, {}, true)
          }
        />
      </div>
    );
  };

  return (
    <div>
      {/* Visible fields */}
      {visibleFields.map((field, index) => renderVisibleField(field, index))}
    </div>
  );
};

export default FieldConfigRenderer;
